import { useState, useRef, useEffect } from "react";

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Palette en dur, indépendante de tout scope CSS (.exp-public / .pp-landing) :
// ce composant est monté à la fois sous Login/Register (.exp-public) et sous
// Landing (.pp-landing), et son rendu utilise un Fragment (deux racines DOM
// position:fixed distinctes, pas un unique <div> englobant), donc pas de
// classe wrapper unique possible pour scoper des variables CSS. Des valeurs
// en dur évitent complètement le bug de collision :root déjà rencontré sur
// la landing (cf. commit correctif contain:paint / .pp-landing).
const BRAND        = "#22d15b"; // vert de marque (design-tokens.css)
const BRAND_HOVER  = "#1ebd52"; // même valeur que design-tokens.css --brand-hover
const ON_BRAND     = "#08130c"; // texte/icônes sur fond vert marque — contraste ~8-10:1,
                                 // même choix que les boutons CTA de Landing.jsx (le blanc
                                 // n'a qu'un contraste ~2:1 sur ce vert, illisible)
const INK          = "#14171b"; // fond sombre de marque, badge de l'avatar
const FONT_HEAD    = "'Manrope', system-ui, sans-serif";
const FONT_BODY    = "'Inter', system-ui, sans-serif";

const SUGGESTIONS = [
  { label: "💰 Voir les tarifs",         msg: "Quels sont vos tarifs ?" },
  { label: "🚀 Essai gratuit",            msg: "Comment démarrer un essai gratuit ?" },
  { label: "📋 Les fonctionnalités",      msg: "Quelles sont les fonctionnalités principales ?" },
  { label: "🆚 Comparatif concurrents",   msg: "Comment vous comparez-vous à Pennylane et Dolibarr ?" },
];

const WELCOME_MESSAGE = {
  role: "assistant",
  content: "Bonjour ! 👋 Je suis Max, de l'équipe ExecPro360.\nComment puis-je vous aider aujourd'hui ?\n\nVous pouvez me poser des questions sur nos fonctionnalités, nos tarifs, ou démarrer votre essai gratuit de 14 jours !",
};

function genSessionId() {
  return 'sess_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function Chatbot({ sourcePage = 'landing' }) {
  const [open, setOpen]               = useState(false);
  const [messages, setMessages]       = useState([WELCOME_MESSAGE]);
  const [input, setInput]             = useState("");
  const [loading, setLoading]         = useState(false);
  const [unread, setUnread]           = useState(0);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadForm, setLeadForm]       = useState({ name: "", email: "", company: "", plan_interested: "" });
  const [leadSent, setLeadSent]       = useState(false);
  const [sessionId]                   = useState(() => genSessionId());
  const bottomRef                     = useRef(null);
  const inputRef                      = useRef(null);

  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  async function sendMessage(text) {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg }]);
    setLoading(true);

    try {
      const res = await fetch(`${BASE}/chatbot/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg, page: sourcePage }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: "assistant", content: data.reply || "Désolé, une erreur est survenue." }]);
      if (!open) setUnread(u => u + 1);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Je suis temporairement indisponible. Réessayez dans un moment." }]);
    } finally {
      setLoading(false);
    }
  }

  async function sendLead() {
    if (!leadForm.email) return;
    try {
      await fetch(`${BASE}/chatbot/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...leadForm, session_id: sessionId }),
      });
      setLeadSent(true);
      setShowLeadForm(false);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: `Merci ${leadForm.name || ""} ! 🎉 Nous avons bien noté votre intérêt. Un membre de notre équipe vous contactera à l'adresse ${leadForm.email} pour organiser une démo personnalisée.`,
      }]);
    } catch {}
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  }

  const bubbleStyle = (role) => ({
    maxWidth: "85%",
    padding: "9px 13px",
    borderRadius: role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
    background: role === "user" ? BRAND : "#f1f5f9",
    color: role === "user" ? ON_BRAND : "#1e293b",
    fontFamily: FONT_BODY,
    fontSize: 13,
    lineHeight: 1.55,
    alignSelf: role === "user" ? "flex-end" : "flex-start",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  });

  return (
    <>
      {/* Bouton flottant */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 9000,
          width: 56, height: 56, borderRadius: "50%",
          background: BRAND, color: ON_BRAND, fontSize: 24,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", boxShadow: "0 4px 20px rgba(34,209,91,0.45)",
          transition: "transform 0.2s, box-shadow 0.2s",
          transform: open ? "rotate(180deg)" : "none",
        }}
        title="Max — Équipe ExecPro360"
      >
        {open ? "✕" : "💬"}
        {!open && unread > 0 && (
          <span style={{
            position: "absolute", top: -4, right: -4,
            background: "#e24b4a", color: "#fff",
            borderRadius: "50%", width: 20, height: 20,
            fontSize: 11, fontWeight: 700, fontFamily: FONT_HEAD,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>{unread}</span>
        )}
      </div>

      {/* Fenêtre chat */}
      {open && (
        <div style={{
          position: "fixed", bottom: 90, right: 24, zIndex: 9000,
          width: 350, height: 500, borderRadius: 16,
          background: "#fff", boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
          display: "flex", flexDirection: "column", overflow: "hidden",
          border: "1px solid #e2e8f0",
          animation: "chatSlideIn 0.2s ease",
        }}>

          {/* En-tête */}
          <div style={{ background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_HOVER} 100%)`, padding: "14px 16px", color: ON_BRAND }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: INK, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                <img src="/assets/logo-icon-execpro360.png" alt="" style={{ width: 24, height: 24, objectFit: "contain" }} />
              </div>
              <div>
                <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: 14 }}>Max</div>
                <div style={{ fontFamily: FONT_BODY, fontSize: 11, opacity: 0.75 }}>Équipe ExecPro360 · Réponse en quelques secondes</div>
              </div>
              <div style={{ marginLeft: "auto", width: 8, height: 8, borderRadius: "50%", background: "#fff", boxShadow: "0 0 0 2px rgba(255,255,255,0.35)" }} />
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
            {messages.map((m, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: m.role === "user" ? "flex-end" : "flex-start" }}>
                <div style={bubbleStyle(m.role)}>{m.content}</div>
              </div>
            ))}

            {/* Suggestions (après le message de bienvenue seulement) */}
            {messages.length === 1 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                {SUGGESTIONS.map(s => (
                  <button key={s.label} onClick={() => sendMessage(s.msg)}
                    style={{ fontFamily: FONT_HEAD, textAlign: "left", padding: "7px 12px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#f8fafc", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#334155", transition: "background 0.15s" }}>
                    {s.label}
                  </button>
                ))}
              </div>
            )}

            {loading && (
              <div style={{ alignSelf: "flex-start" }}>
                <div style={{ ...bubbleStyle("assistant"), padding: "8px 14px" }}>
                  <span style={{ display: "inline-flex", gap: 4 }}>
                    {[0, 1, 2].map(i => (
                      <span key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: "#94a3b8", animation: `bounce 1s ${i * 0.2}s infinite` }} />
                    ))}
                  </span>
                </div>
              </div>
            )}

            {/* Formulaire lead */}
            {showLeadForm && !leadSent && (
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: 12, marginTop: 4 }}>
                <div style={{ fontFamily: FONT_HEAD, fontWeight: 600, fontSize: 13, marginBottom: 8 }}>📋 Vos coordonnées</div>
                {[
                  { key: "name",   placeholder: "Votre prénom/nom",       type: "text" },
                  { key: "email",  placeholder: "Votre email *",           type: "email" },
                  { key: "company",placeholder: "Votre société",           type: "text" },
                ].map(f => (
                  <input key={f.key} type={f.type} placeholder={f.placeholder}
                    value={leadForm[f.key]} onChange={e => setLeadForm(lf => ({ ...lf, [f.key]: e.target.value }))}
                    style={{ fontFamily: FONT_BODY, width: "100%", padding: "6px 10px", borderRadius: 7, border: "1px solid #e2e8f0", fontSize: 12, marginBottom: 6, boxSizing: "border-box" }} />
                ))}
                <select value={leadForm.plan_interested} onChange={e => setLeadForm(lf => ({ ...lf, plan_interested: e.target.value }))}
                  style={{ fontFamily: FONT_BODY, width: "100%", padding: "6px 10px", borderRadius: 7, border: "1px solid #e2e8f0", fontSize: 12, marginBottom: 8, boxSizing: "border-box" }}>
                  <option value="">Nombre d'utilisateurs (optionnel)</option>
                  <option value="1-3">1 à 3 utilisateurs</option>
                  <option value="4-10">4 à 10 utilisateurs</option>
                  <option value="11-25">11 à 25 utilisateurs</option>
                  <option value="26+">Plus de 25 utilisateurs</option>
                </select>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => setShowLeadForm(false)} style={{ fontFamily: FONT_HEAD, flex: 1, padding: "6px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", fontSize: 12, cursor: "pointer" }}>Annuler</button>
                  <button onClick={sendLead} style={{ fontFamily: FONT_HEAD, flex: 2, padding: "6px", borderRadius: 7, border: "none", background: BRAND, color: ON_BRAND, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Envoyer</button>
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Option parler à un humain */}
          {!showLeadForm && !leadSent && messages.length > 3 && (
            <div style={{ padding: "4px 14px", borderTop: "1px solid #f1f5f9" }}>
              <button onClick={() => setShowLeadForm(true)}
                style={{ fontFamily: FONT_BODY, width: "100%", padding: "5px", background: "none", border: "none", fontSize: 11, color: "#64748b", cursor: "pointer", textDecoration: "underline" }}>
                👤 Parler à un humain — laisser mes coordonnées
              </button>
            </div>
          )}

          {/* Champ de saisie */}
          <div style={{ padding: "10px 12px", borderTop: "1px solid #f1f5f9", display: "flex", gap: 8 }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Écrivez votre message…"
              disabled={loading}
              style={{
                fontFamily: FONT_BODY,
                flex: 1, padding: "8px 12px", borderRadius: 20,
                border: "1px solid #e2e8f0", fontSize: 13, outline: "none",
                background: "#f8fafc",
              }}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              style={{
                width: 36, height: 36, borderRadius: "50%", border: "none",
                background: input.trim() && !loading ? BRAND : "#e2e8f0",
                color: input.trim() && !loading ? ON_BRAND : "#94a3b8",
                cursor: input.trim() && !loading ? "pointer" : "default",
                fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center",
                transition: "background 0.15s", flexShrink: 0,
              }}
            >➤</button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes chatSlideIn {
          from { opacity: 0; transform: translateY(16px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-5px); }
        }
      `}</style>
    </>
  );
}
